import { pgTable, text, uuid, timestamp, pgEnum, boolean, jsonb, varchar } from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports (relative — NEVER use @shared/ alias, it breaks esbuild)
import { deals } from "./schema-deals.js";

// ============================================================================
// WORKFLOW MODULE - SPRINT-18
// Deal audit log and comments for activity timeline and team collaboration
// PRD Reference: docs/prds/PRD-DEAL-WORKFLOW.md (planned)
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export const auditEventTypeEnum = pgEnum("audit_event_type_enum", [
  "deal_created", "deal_updated", "deal_status_changed", "deal_assigned",
  "scenario_promoted_to_deal",
  "facility_created", "facility_updated", "facility_status_changed",
  "document_uploaded", "document_status_changed", "document_deleted",
  "task_created", "task_completed", "task_deleted",
  "comment_added", "comment_edited", "comment_deleted",
  "party_added", "party_removed",
  "security_added", "security_removed",
  "stage_changed",
  "application_status_updated",
]);

// ============================================================================
// TABLES
// ============================================================================

// Deal Audit Log: immutable event log for deal activity timeline
export const dealAuditLog = pgTable("crm_x_deal_audit_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  dealId: uuid("deal_id").notNull().references(() => deals.id, { onDelete: "cascade" }),
  eventType: auditEventTypeEnum("event_type").notNull(),
  eventDescription: text("event_description"),
  performedBy: text("performed_by"),
  performedByName: text("performed_by_name"),
  performedAt: timestamp("performed_at", { withTimezone: true }).defaultNow(),
  previousValue: jsonb("previous_value"),
  newValue: jsonb("new_value"),
  relatedEntityType: text("related_entity_type"),
  relatedEntityId: uuid("related_entity_id"),
  organisationId: uuid("organisation_id").notNull(),
});

// Deal Comments: team discussion threads on deals
export const dealComments = pgTable("crm_x_deal_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  dealId: uuid("deal_id").notNull().references(() => deals.id, { onDelete: "cascade" }),
  commentText: text("comment_text").notNull(),
  isInternal: boolean("is_internal").default(true),
  parentCommentId: uuid("parent_comment_id"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  isEdited: boolean("is_edited").default(false),
  organisationId: uuid("organisation_id").notNull(),
});

// Comment Mentions: @mention tracking for deal comments (Sprint 28 F015)
export const commentMentions = pgTable("crm_x_comment_mentions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  commentId: uuid("comment_id").notNull().references(() => dealComments.id, { onDelete: "cascade" }),
  mentionedUserId: text("mentioned_user_id").notNull(),
  mentionedUserName: varchar("mentioned_user_name", { length: 255 }).notNull(),
  notifiedAt: timestamp("notified_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// RELATIONS
// ============================================================================

export const dealAuditLogRelations = relations(dealAuditLog, ({ one }) => ({
  deal: one(deals, {
    fields: [dealAuditLog.dealId],
    references: [deals.id],
  }),
}));

export const dealCommentsRelations = relations(dealComments, ({ one, many }) => ({
  deal: one(deals, {
    fields: [dealComments.dealId],
    references: [deals.id],
  }),
  parentComment: one(dealComments, {
    fields: [dealComments.parentCommentId],
    references: [dealComments.id],
  }),
  mentions: many(commentMentions),
}));

export const commentMentionsRelations = relations(commentMentions, ({ one }) => ({
  comment: one(dealComments, {
    fields: [commentMentions.commentId],
    references: [dealComments.id],
  }),
}));

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const insertDealAuditLogSchema = createInsertSchema(dealAuditLog, {
  dealId: z.string().uuid(),
  eventType: z.enum([
    "deal_created", "deal_updated", "deal_status_changed", "deal_assigned",
    "scenario_promoted_to_deal",
    "facility_created", "facility_updated", "facility_status_changed",
    "document_uploaded", "document_status_changed", "document_deleted",
    "task_created", "task_completed", "task_deleted",
    "comment_added", "comment_edited", "comment_deleted",
    "party_added", "party_removed",
    "security_added", "security_removed",
    "stage_changed",
  ]),
  organisationId: z.string().uuid(),
  performedBy: z.string().optional().nullable(),
  performedByName: z.string().optional().nullable(),
  eventDescription: z.string().optional().nullable(),
  previousValue: z.any().optional().nullable(),
  newValue: z.any().optional().nullable(),
  relatedEntityType: z.string().optional().nullable(),
  relatedEntityId: z.string().uuid().optional().nullable(),
  performedAt: z.coerce.date().optional().nullable(),
});

export const insertDealCommentSchema = createInsertSchema(dealComments, {
  dealId: z.string().uuid(),
  commentText: z.string().min(1),
  organisationId: z.string().uuid().optional().nullable(),
  parentCommentId: z.string().uuid().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  isInternal: z.boolean().optional().nullable(),
  isEdited: z.boolean().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
});

export const insertCommentMentionSchema = createInsertSchema(commentMentions, {
  commentId: z.string().uuid(),
  mentionedUserId: z.string().min(1),
  mentionedUserName: z.string().min(1).max(255),
  notifiedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
});

export const selectCommentMentionSchema = createSelectSchema(commentMentions);

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type DealAuditLog = typeof dealAuditLog.$inferSelect;
export type InsertDealAuditLog = typeof dealAuditLog.$inferInsert;

export type DealComment = typeof dealComments.$inferSelect;
export type InsertDealComment = typeof dealComments.$inferInsert;

export type CommentMention = typeof commentMentions.$inferSelect;
export type InsertCommentMention = typeof commentMentions.$inferInsert;
